/**
 * frontend/src/components/workspace/ConsoleManager.jsx
 * -------------------------------------------------------
 * Gestionnaire de consoles multi-onglets (Telnet + VNC).
 *
 * CORRECTIFS :
 *  - API (openTelnet, openVnc) exposée via onApiReady(ref) au lieu de window events globaux
 *  - Plus de listeners window.__cm_listeners_installed (fragile, non nettoyé)
 *  - closeTab utilise un index stable via key (pas de désynchronisation)
 *  - ConsoleManager accepte onApiReady pour que LabWorkspacePage branche ses callbacks
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import TelnetConsolePanel from "../console/TelnetConsolePanel";
import NoVncViewer        from "../console/NoVncViewer";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const norm = (s) => String(s || "").toLowerCase().trim();
const isTelnetNode = (n) => norm(n?.console_type) === "telnet" && Number(n?.console_port) > 0;
const isVncNode    = (n) => norm(n?.console_type) === "vnc"    && Number(n?.console_port) > 0;

// ---------------------------------------------------------------------------
// Composant
// ---------------------------------------------------------------------------

/**
 * @param {object} props
 * @param {number}   props.reservationId
 * @param {string}   props.host           — IP GNS3 console (atteignable depuis le client)
 * @param {object[]} props.nodes           — liste des nœuds GNS3
 * @param {function} props.onApiReady      — appelé avec { openTelnet, openVnc }
 */
export default function ConsoleManager({ reservationId, host, nodes, onApiReady }) {
  // tabs : [{ key, type, nodeId, name, port }]
  const [tabs,   setTabs]   = useState([]);
  const [active, setActive] = useState(0);

  const nodesById = useMemo(() => {
    const m = new Map();
    (nodes || []).forEach((n) => m.set(n.node_id, n));
    return m;
  }, [nodes]);

  // ---------------------------------------------------------------------------
  // Ouvrir / fermer onglets
  // ---------------------------------------------------------------------------

  const openTab = useCallback((type, node) => {
    if (!node) return;
    const key = `${type}:${node.node_id}:${node.console_port}`;
    setTabs((prev) => {
      const existing = prev.findIndex((t) => t.key === key);
      if (existing >= 0) {
        setActive(existing);
        return prev;
      }
      const next = [
        ...prev,
        {
          key,
          type,
          nodeId: node.node_id,
          name:   node.name,
          port:   node.console_port,
        },
      ];
      setActive(next.length - 1);
      return next;
    });
  }, []);

  const openTelnet = useCallback((node) => {
    if (!isTelnetNode(node)) return;
    openTab("telnet", node);
  }, [openTab]);

  const openVnc = useCallback((node) => {
    if (!isVncNode(node)) return;
    openTab("vnc", node);
  }, [openTab]);

  const closeTab = useCallback((key) => {
    setTabs((prev) => {
      const idx  = prev.findIndex((t) => t.key === key);
      const next = prev.filter((t) => t.key !== key);
      setActive((a) => {
        if (next.length === 0) return 0;
        if (idx < a) return a - 1;
        if (idx === a) return Math.min(a, next.length - 1);
        return a;
      });
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Exposer l'API au parent via onApiReady
  // ---------------------------------------------------------------------------

  useEffect(() => {
    onApiReady?.({ openTelnet, openVnc });
  }, [onApiReady, openTelnet, openVnc]);

  // ---------------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------------

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography fontWeight={700}>Consoles</Typography>
        <Chip size="small" label={`${tabs.length} onglet(s)`} />
      </Stack>

      {tabs.length === 0 ? (
        <Typography variant="body2" sx={{ opacity: 0.6 }}>
          Ouvrez une console via les boutons <strong>Telnet</strong> / <strong>VNC</strong>
          {" "}sur un nœud, ou en cliquant un nœud dans la topologie.
        </Typography>
      ) : (
        <>
          {/* Barre d'onglets */}
          <Tabs
            value={active}
            onChange={(_, v) => setActive(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 1, borderBottom: "1px solid rgba(0,0,0,0.1)" }}
          >
            {tabs.map((t, idx) => (
              <Tab
                key={t.key}
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2" sx={{ textTransform: "none" }}>
                      {t.name} — {t.type}:{t.port}
                    </Typography>
                    {/* Bouton fermer sur l'onglet */}
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        ml: 0.5,
                        opacity: 0.5,
                        "&:hover": { opacity: 1, color: "error.main" },
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        closeTab(t.key);
                      }}
                      title="Fermer"
                    >
                      ✕
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </Tabs>

          {/* Contenu des onglets : tous restent montés une fois ouverts (visibilité
              togglée en CSS, jamais démontés tant que l'onglet n'est pas fermé via ✕).
              RC-03 : un retour sur un onglet déjà ouvert doit retrouver le même buffer
              terminal et la même connexion WebSocket — pas une reconnexion à froid. */}
          {tabs.map((t, idx) => {
            const node = nodesById.get(t.nodeId);
            const consoleHost = node?.console_host || host;
            const hidden = idx !== active;

            if (t.type === "telnet") {
              return (
                <Box key={t.key} sx={{ display: hidden ? "none" : "block" }}>
                  <TelnetConsolePanel
                    host={consoleHost}
                    port={t.port}
                  />
                </Box>
              );
            }

            if (t.type === "vnc") {
              return (
                <Box key={t.key} sx={{ display: hidden ? "none" : "block" }}>
                  <NoVncViewer
                    host={consoleHost}
                    port={t.port}
                    label={`${t.name} VNC`}
                  />
                </Box>
              );
            }

            return null;
          })}
        </>
      )}
    </Paper>
  );
}
